//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import {
  Dialog, DialogContent, DialogTitle, IconButton, Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BrandedTitleBar from '@/components/Elements/BrandedTitleBar';
import PluginSurfaceFrame from '@/components/Elements/PluginSurfaceFrame';
import {
  type ActivePlugin, type PluginWebviewSurface
} from '@/services/apiTypes';

interface PluginIframeModalProps {
  plugin: ActivePlugin;
  surface: PluginWebviewSurface;
  onClose: () => void;
}

/**
 * Renders a plugin's HTML surface, drawn as the contents of a modal dialog.
 *
 * The host only owns the chrome (title bar, close button, modal frame).
 * One dismissal, in the corner: a second control that did the same thing
 * is what a panel's title bar was cut down to avoid.
 * Sizing is fixed (large dialog, 70vh tall) — auto-resizing to content would
 * require a postMessage protocol that v0.0.1 deliberately does not define.
 */
function PluginIframeModal(
  { plugin, surface, onClose }: PluginIframeModalProps
) {
  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      aria-labelledby="plugin-iframe-modal-title"
    >
      <DialogTitle>
        <BrandedTitleBar
          title={surface.label || plugin.name}
          titleId="plugin-iframe-modal-title"
          actions={(
            <Tooltip describeChild title="Close">
              <span>
                <IconButton
                  onClick={onClose}
                  aria-label="Close plugin surface"
                >
                  <CloseIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        />
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, height: '70vh' }}>
        <PluginSurfaceFrame
          url={surface.url}
          title={surface.label || `${plugin.name} surface`}
        />
      </DialogContent>
    </Dialog>
  );
}

export default PluginIframeModal;
